import { css } from 'aphrodite';
import * as React from 'react';
import { Button } from '../../../components-v2/Button';
import { TabToggle } from '../../../components-v2/TabToggle';
import { ChevronLeft } from '../../../components/svgs/icons/ChevronLeft';
import { ChevronRight } from '../../../components/svgs/icons/ChevronRight';
import { styleSheet } from '../styles';
import { CALENDAR_VIEW_MODES, CalendarViewMode } from '../types';

/** Month/week/day switcher options. */
const CALENDAR_VIEW_ITEMS: { value: CalendarViewMode; label: string }[] = [
	{ value: 'month', label: 'Month' },
	{ value: 'week', label: 'Week' },
	{ value: 'day', label: 'Day' },
];

interface IProps {
	viewMode: CalendarViewMode;
	onViewModeChange: (mode: CalendarViewMode) => void;
	title: string;
	onPrevious: () => void;
	onNext: () => void;
	onToday: () => void;
	/** Heading level for the range title (standalone page uses h1; the board nests under its own heading). */
	titleTag?: 'h1' | 'h2';
	/** Extra controls (legend, user picker) placed in the right group before the view switcher. */
	children?: React.ReactNode;
}

/**
 * Shared calendar header: range title, prev/next/today navigation, and the month/week/day
 * switcher. The standalone calendar and the Meetings-board calendar both build on this;
 * surface-specific controls are passed as {@link IProps.children}.
 */
export const CalendarViewToolbar: React.FC<IProps> = ({
	viewMode,
	onViewModeChange,
	title,
	onPrevious,
	onNext,
	onToday,
	titleTag = 'h1',
	children,
}) => {
	const TitleTag = titleTag;
	return (
		<div className={css(styleSheet.toolbar)}>
			<div className={css(styleSheet.toolbarGroup)}>
				<Button variant='outline' onClick={onToday}>
					Today
				</Button>
				<Button
					variant='ghost'
					size='icon'
					aria-label='Previous'
					styles={[styleSheet.navIconButton]}
					onClick={onPrevious}
				>
					<ChevronLeft width={16} height={16} />
				</Button>
				<Button variant='ghost' size='icon' aria-label='Next' styles={[styleSheet.navIconButton]} onClick={onNext}>
					<ChevronRight width={16} height={16} />
				</Button>
				<TitleTag className={css(styleSheet.title)}>{title}</TitleTag>
			</div>
			<div className={css(styleSheet.toolbarGroup)}>
				{children}
				<TabToggle
					items={CALENDAR_VIEW_ITEMS}
					value={viewMode}
					styles={[styleSheet.viewToggle]}
					onValueChange={value => {
						// TabToggle hands back a plain string; narrow it against the known modes
						// rather than force-casting, so an unexpected value is ignored, not trusted.
						const mode = CALENDAR_VIEW_MODES.find(m => m === value);
						if (mode) {
							onViewModeChange(mode);
						}
					}}
				/>
			</div>
		</div>
	);
};
